import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import type { RivhitCustomer } from '../context/RivhitDataContext';
import CustomerDetailsModal from '../components/CustomerDetailsModal';

const BATCH_SIZE = 40;
type CustomerSortField = 'name' | 'city' | 'id';
type SortDirection = 'asc' | 'desc';

interface NewCustomerForm {
  last_name: string;
  first_name: string;
  phone: string;
  email: string;
  city: string;
  street: string;
}

const emptyForm: NewCustomerForm = {
  last_name: '',
  first_name: '',
  phone: '',
  email: '',
  city: '',
  street: '',
};

function fuzzyMatch(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (t.includes(q)) return 0;

  let qi = 0;
  let score = 0;
  let lastMatchIdx = -1;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatchIdx >= 0) {
        score += ti - lastMatchIdx - 1;
      }
      lastMatchIdx = ti;
      qi++;
    }
  }

  return qi === q.length ? score + 1 : -1;
}

function fuzzySearch(customers: RivhitCustomer[], query: string): RivhitCustomer[] {
  if (!query.trim()) return customers;

  const q = query.trim();

  const scored = customers
    .map((customer) => {
      const fields = [
        rivhitDisplayName(customer),
        customer.last_name,
        customer.first_name,
        customer.phone,
        customer.email,
        customer.city,
        customer.street,
        customer.id_number != null ? String(customer.id_number) : undefined,
      ].filter(Boolean) as string[];

      let bestScore = -1;
      for (const field of fields) {
        const score = fuzzyMatch(q, field);
        if (score >= 0 && (bestScore < 0 || score < bestScore)) {
          bestScore = score;
        }
      }

      return { customer, score: bestScore };
    })
    .filter((item) => item.score >= 0)
    .sort((a, b) => a.score - b.score);

  return scored.map((item) => item.customer);
}

export default function Customers() {
  const { t } = useTranslation();
  const {
    configured,
    customers: allCustomers,
    customersLoading: loading,
    ensureCustomersLoaded,
    refreshCustomers,
    createCustomer,
  } = useRivhitData();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<CustomerSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedCustomer, setSelectedCustomer] = useState<RivhitCustomer | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newForm, setNewForm] = useState<NewCustomerForm>(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const filtered = useMemo(() => {
    const results = fuzzySearch(allCustomers, debouncedQuery);
    results.sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'city':
          result = (a.city || '').localeCompare(b.city || '', 'he') || rivhitDisplayName(a).localeCompare(rivhitDisplayName(b), 'he');
          break;
        case 'id':
          result = a.customer_id - b.customer_id;
          break;
        case 'name':
        default:
          result = rivhitDisplayName(a).localeCompare(rivhitDisplayName(b), 'he');
          break;
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return results;
  }, [allCustomers, debouncedQuery, sortDirection, sortField]);

  const handleSort = (field: CustomerSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'id' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (field: CustomerSortField) => {
    if (field !== sortField) return <span className="text-gray-300">↕</span>;
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const { visible: paged, hasMore, sentinelRef } = useInfiniteScroll(filtered, BATCH_SIZE);

  const handleCreateCustomer = async () => {
    if (!newForm.last_name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const payload: Record<string, unknown> = { last_name: newForm.last_name.trim() };
      if (newForm.first_name.trim()) payload.first_name = newForm.first_name.trim();
      if (newForm.phone.trim()) payload.phone = newForm.phone.trim();
      if (newForm.email.trim()) payload.email = newForm.email.trim();
      if (newForm.city.trim()) payload.city = newForm.city.trim();
      if (newForm.street.trim()) payload.street = newForm.street.trim();
      await createCustomer(payload as any);
      setShowAddModal(false);
      setNewForm(emptyForm);
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || err?.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('customers.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshCustomers}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('customers.refresh', 'Refresh customers from Rivhit')}
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            {t('customers.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400 pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('customers.searchPlaceholder')}
              className="w-full rounded-md border border-gray-300 bg-gray-50 ltr:pl-10 rtl:pr-10 ltr:pr-3 rtl:pl-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs font-medium uppercase tracking-wider text-gray-500">
          <button type="button" onClick={() => handleSort('name')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>{t('customers.title')}</span>
            {renderSortIndicator('name')}
          </button>
          <button type="button" onClick={() => handleSort('city')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>{t('customers.city')}</span>
            {renderSortIndicator('city')}
          </button>
          <button type="button" onClick={() => handleSort('id')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>ID</span>
            {renderSortIndicator('id')}
          </button>
        </div>
        {debouncedQuery && !loading && (
          <p className="mt-2 text-xs text-gray-500">
            {filtered.length} {t('customers.resultsFound')}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm">{t('common.loading')}</div>
      ) : paged.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">{t('customers.noResults')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {paged.map((customer) => (
              <button
                key={customer.customer_id}
                onClick={() => setSelectedCustomer(customer)}
                className={`text-start rounded-lg border p-4 transition-all hover:shadow-md ${
                  selectedCustomer?.customer_id === customer.customer_id
                    ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {rivhitDisplayName(customer).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">{rivhitDisplayName(customer)}</h3>
                    {customer.phone && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <span>📞</span> {customer.phone}
                      </p>
                    )}
                    {customer.city && (
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        <span>📍</span> {customer.city}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <span className="text-sm text-gray-400">
                {paged.length} / {filtered.length}
              </span>
            </div>
          )}

          {!hasMore && filtered.length > BATCH_SIZE && (
            <p className="text-center text-xs text-gray-400 py-2">
              {filtered.length} {t('customers.resultsFound')}
            </p>
          )}
        </>
      )}

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">{t('customers.addCustomer', 'Add Customer')}</h2>
              <button
                onClick={() => { setShowAddModal(false); setCreateError(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('customers.lastName', 'Last Name / Company')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newForm.last_name}
                  onChange={(e) => setNewForm((form) => ({ ...form, last_name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  placeholder={t('customers.lastNamePlaceholder', 'Enter last name or company name')}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('customers.firstName', 'First Name')}
                </label>
                <input
                  type="text"
                  value={newForm.first_name}
                  onChange={(e) => setNewForm((form) => ({ ...form, first_name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  placeholder={t('customers.firstNamePlaceholder', 'Enter first name')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.phone')}
                  </label>
                  <input
                    type="tel"
                    value={newForm.phone}
                    onChange={(e) => setNewForm((form) => ({ ...form, phone: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.phonePlaceholder', 'e.g. 050-1234567')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.email')}
                  </label>
                  <input
                    type="email"
                    value={newForm.email}
                    onChange={(e) => setNewForm((form) => ({ ...form, email: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.emailPlaceholder', 'email@example.com')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.city')}
                  </label>
                  <input
                    type="text"
                    value={newForm.city}
                    onChange={(e) => setNewForm((form) => ({ ...form, city: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.cityPlaceholder', 'Enter city')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.street', 'Street')}
                  </label>
                  <input
                    type="text"
                    value={newForm.street}
                    onChange={(e) => setNewForm((form) => ({ ...form, street: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.streetPlaceholder', 'Enter street address')}
                  />
                </div>
              </div>

              {createError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {createError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddModal(false); setCreateError(null); setNewForm(emptyForm); }}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateCustomer}
                disabled={creating || !newForm.last_name.trim()}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creating ? t('customers.creating', 'Creating...') : t('customers.create', 'Create Customer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
